import React from 'react';
import Button from '../../../shared/Button';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';

export default function WidgetEditorShell({
  site,
  widgetType,
  widgetId,
  queryWidgetType,
  saving,
  activeTab,
  setActiveTab,
  onSave,
  onCancel,
  updateWidgetType,
  tabs,
  children,
  previewContent,
  siteId,
  showErrorDialog,
  setShowErrorDialog,
  validationErrors,
  hidePreview,
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#F2F1F1', overflow: 'hidden', fontFamily: "'Open Sans', sans-serif" }}>
      {/* Top Header */}
      <div style={{
        height: '64px',
        background: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        borderBottom: '1px solid #E0E0E0',
        zIndex: 100,
        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div
            onClick={onCancel}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#333',
              width: '32px',
              height: '32px',
              borderRadius: '16px',
              transition: 'background 0.2s'
            }}
          >
            <CloseIcon />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 'bold', fontSize: '18px', color: '#23262A' }}>
              {widgetId === 'new'
                ? (widgetType === 'ai_summary' ? 'Create AI Summary Button' : 'Create Chat Widget')
                : (widgetType === 'ai_summary' ? 'Edit AI Summary Button' : 'Edit Chat Widget')}
            </span>
            <span style={{ fontSize: '12px', color: '#777' }}>{site.url}</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            variant="outlined"
            label="Cancel"
            onClick={onCancel}
            style={{ color: '#666', border: '1px solid #ccc', background: 'white' }}
          />
          <Button
            variant="contained"
            label={saving ? 'Saving...' : 'Save & Publish'}
            disabled={saving}
            onClick={onSave}
            style={{ background: '#ED5A29', color: 'white', fontWeight: 'bold', padding: '0 25px' }}
          />
        </div>
      </div>

      {widgetId === 'new' && !queryWidgetType && (
        <div style={{
          display: 'flex', justifyContent: 'center', gap: '16px', padding: '16px 24px',
          background: '#FAFBFC', borderBottom: '1px solid #E0E0E0'
        }}>
          {[
            { widget_type: 'chat', icon: '💬', label: 'Chat Widget', desc: 'Floating bubble with messaging channels' },
            { widget_type: 'ai_summary', icon: '🤖', label: 'AI Summary Button', desc: 'Inline AI provider buttons on your content' },
          ].map(opt => (
            <div
              key={opt.widget_type}
              onClick={() => updateWidgetType(opt.widget_type)}
              style={{
                flex: 1, maxWidth: '400px', padding: '16px 20px', borderRadius: '12px', cursor: 'pointer',
                border: widgetType === opt.widget_type ? '2px solid #ED5A29' : '2px solid #E0E0E0',
                background: widgetType === opt.widget_type ? '#FFF5F2' : '#fff',
                transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '16px',
                boxShadow: widgetType === opt.widget_type ? '0 4px 12px rgba(237,90,41,0.1)' : 'none'
              }}
            >
              <div style={{ fontSize: '36px' }}>{opt.icon}</div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#23262A' }}>{opt.label}</div>
                <div style={{ fontSize: '13px', color: '#777', marginTop: '2px' }}>{opt.desc}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* PANE 1: COMPACT SIDEBAR */}
        <div style={{
          width: '85px',
          background: '#2C3E50',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '20px 0',
          gap: '10px',
          flexShrink: 0
        }}>
          {tabs.map(tab => (
            <div
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                width: '65px',
                height: '65px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderRadius: '10px',
                gap: '4px',
                transition: 'all 0.2s',
                background: activeTab === tab.id ? '#ED5A29' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#BDC3C7'
              }}
            >
              {React.cloneElement(tab.icon, { style: { fontSize: '24px' } })}
              <span style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase' }}>{tab.label}</span>
            </div>
          ))}
        </div>

        {/* PANE 2: ACTIVE EDITOR PANEL */}
        <div
          className="hb-editor-sidebar"
          style={{
            width: '400px',
            background: 'white',
            borderRight: '1px solid #E5E7EB',
            padding: '0',
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '4px 0 10px rgba(0,0,0,0.02)',
            flexShrink: 0,
            fontFamily: "'Montserrat', sans-serif"
          }}
        >
          <div style={{ padding: '24px', borderBottom: '1px solid #F3F4F6', background: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#111827', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {(tabs.find(t => t.id === activeTab) || {}).label || activeTab}
            </h2>
          </div>

          <div style={{ padding: '24px' }}>
            {children}
          </div>
        </div>

        {/* Preview Panel */}
        <div style={{ flex: 1, position: 'relative', background: '#F2F1F1', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{
            width: '100%',
            height: '100%',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.1)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #E0E0E0'
          }}>
            {/* Browser Header */}
            <div style={{ height: '54px', background: '#F8F9FA', borderBottom: '1px solid #E0E0E0', display: 'flex', alignItems: 'center', padding: '0 20px', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ width: 12, height: 12, borderRadius: 6, background: '#FF5F56' }}></div>
                <div style={{ width: 12, height: 12, borderRadius: 6, background: '#FFBD2E' }}></div>
                <div style={{ width: 12, height: 12, borderRadius: 6, background: '#27C93F' }}></div>
              </div>
              <div style={{ flex: 1, height: '32px', background: 'white', borderRadius: '6px', border: '1px solid #E0E0E0', margin: '0 20px', display: 'flex', alignItems: 'center', padding: '0 15px', fontSize: '13px', color: '#666' }}>
                <span style={{ color: '#aaa', marginRight: '4px' }}>https://</span>{site.url?.replace(/^https?:\/\//, '') || 'yoursite.com'}
              </div>
            </div>

            {/* Site Content + Preview Overlay */}
            <div style={{ flex: 1, position: 'relative', overflowY: 'auto', background: hidePreview ? '#F5F5F5' : '#fff' }}>
              {!hidePreview && (
                <>
                  <img
                    src={`${process.env.REACT_APP_EDITOR_HOST}/site_previews/${siteId}?viewport=1280x1024`}
                    alt="Site Preview"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                  <div style={{ height: '100%', display: 'none', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#DDD', padding: '40px' }}>
                    <div style={{ fontSize: '80px', marginBottom: '20px' }}>🌐</div>
                    <h2 style={{ color: '#BBB', margin: 0 }}>Your Website Preview</h2>
                    <p style={{ color: '#CCC' }}>Configure your widget to see it in action</p>
                  </div>
                  {/* Overlay to dim background */}
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.03)', pointerEvents: 'none' }}></div>
                </>
              )}

              {/* Preview content passed from each editor */}
              {previewContent}
            </div>
          </div>

          <style>{`
            @keyframes slideUp {
              from { opacity: 0; transform: translateY(30px) scale(0.9); }
              to { opacity: 1; transform: translateY(0) scale(1); }
            }
            @keyframes hb-fade-in { from { opacity: 0; } to { opacity: 1; } }
            @keyframes hb-slide-up { from { transform: translateY(50px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            @keyframes hb-bounce {
              0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
              40% {transform: translateY(-30px);}
              60% {transform: translateY(-15px);}
            }
            .hb-editor-sidebar select {
              appearance: none;
              -webkit-appearance: none;
              background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23777%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E") !important;
              background-repeat: no-repeat !important;
              background-position: right 12px center !important;
              background-size: 16px !important;
            }
          `}</style>
        </div>
      </div>

      <Dialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        PaperProps={{
          style: { borderRadius: '15px', padding: '10px' }
        }}
      >
        <DialogTitle style={{ fontWeight: 'bold', color: '#23262A' }}>
          Action Required
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" variant="outlined" style={{ border: 'none', padding: 0, fontSize: '15px' }}>
            {widgetType === 'ai_summary'
              ? (validationErrors.css_selector && validationErrors.providers
                ? "Please add a CSS selector and select at least one AI provider before publishing."
                : validationErrors.css_selector
                  ? "Please add a CSS selector before publishing."
                  : "Please select at least one AI provider before publishing.")
              : (Object.values(validationErrors)[0] === "This field is required"
                ? "Please fill in the required fields for all channels before publishing."
                : "Please correct the invalid fields in your channels.")}
          </Alert>
        </DialogContent>
        <DialogActions style={{ padding: '20px' }}>
          <Button
            onClick={() => setShowErrorDialog(false)}
            color="primary"
            variant="contained"
            style={{ padding: '8px 25px' }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
